import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { 
  ArrowLeft, Ticket, AlertTriangle, AlertCircle, 
  Info, Flame, Wrench, MessageSquare,
  Package, MapPin
} from 'lucide-react';

const priorityOptions = [
  { value: 'low', label: 'Bassa', color: 'bg-gray-100 text-gray-700 border-gray-200', icon: Info },
  { value: 'medium', label: 'Media', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: AlertCircle },
  { value: 'high', label: 'Alta', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: AlertTriangle },
  { value: 'urgent', label: 'Urgente', color: 'bg-red-100 text-red-700 border-red-200', icon: Flame }
];

const typeOptions = [
  { value: 'repair', label: 'Riparazione', icon: Wrench, color: 'from-orange-500 to-orange-600' },
  { value: 'maintenance', label: 'Manutenzione', icon: Wrench, color: 'from-blue-500 to-blue-600' },
  { value: 'inspection', label: 'Ispezione', icon: AlertCircle, color: 'from-purple-500 to-purple-600' },
  { value: 'other', label: 'Altro', icon: MessageSquare, color: 'from-gray-500 to-gray-600' }
];

export default function NewTicket() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const assetId = searchParams.get('asset') || '';

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [type, setType] = useState('repair');
  const [selectedAssetId, setSelectedAssetId] = useState(assetId);
  const [locationId, setLocationId] = useState('');

  const { data: assetsData } = useQuery({
    queryKey: ['assets'],
    queryFn: () => api.getAssets(),
    refetchOnMount: 'always'
  });

  const { data: locationsData } = useQuery({
    queryKey: ['locations'],
    queryFn: api.getLocations,
    refetchOnMount: 'always'
  });

  const createMutation = useMutation({
    mutationFn: api.createTicket,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      navigate(`/tickets/${data.ticket.id}`);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Require at least one of asset OR location
    if (!selectedAssetId && !locationId) {
      alert('È necessario selezionare almeno un asset o una location');
      return;
    }
    
    createMutation.mutate({
      title,
      description: description || undefined,
      priority,
      type,
      assetId: selectedAssetId || undefined,
      locationId: locationId || undefined
    });
  };

  // Check if form is valid
  const isFormValid = title.trim() && (selectedAssetId || locationId);

  return (
    <div className="max-w-2xl mx-auto animate-fadeIn">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/tickets')}
          className="icon-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Ticket className="w-5 h-5 text-primary-500" />
            <span className="text-sm font-medium text-primary-600">Creazione</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Nuovo Ticket</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-6">
        {/* Type Selection */}
        <div className="form-group">
          <label className="form-label">Tipo di Segnalazione</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {typeOptions.map((opt) => {
              const Icon = opt.icon;
              const isSelected = type === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setType(opt.value)}
                  className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                    isSelected
                      ? 'border-primary-500 bg-primary-50 scale-[1.02]'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${opt.color}`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <span className={`text-sm font-medium ${isSelected ? 'text-primary-700' : 'text-gray-700'}`}>
                    {opt.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Titolo *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="input"
            placeholder="Descrivi brevemente il problema..."
            required
            autoFocus
          />
        </div>

        <div className="form-group">
          <label className="form-label">Descrizione</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="input"
            rows={4}
            placeholder="Fornisci dettagli aggiuntivi..."
          />
        </div>

        {/* Priority Selection */}
        <div className="form-group">
          <label className="form-label">Priorità</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {priorityOptions.map((opt) => {
              const Icon = opt.icon;
              const isSelected = priority === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setPriority(opt.value)}
                  className={`p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${opt.color} ${
                    isSelected
                      ? 'ring-2 ring-primary-500 ring-offset-2 scale-[1.02]'
                      : 'opacity-60 hover:opacity-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="form-group">
            <label className="form-label flex items-center gap-2">
              <Package className="w-4 h-4 text-gray-400" />
              Asset Collegato
            </label>
            <select
              value={selectedAssetId}
              onChange={(e) => setSelectedAssetId(e.target.value)}
              className="input"
            >
              <option value="">Nessun asset selezionato</option>
              {assetsData?.assets.map((asset) => (
                <option key={asset.id} value={asset.id}>
                  {asset.name} ({asset.qrCode})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-400" />
              Ubicazione
            </label>
            <select
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              className="input"
            >
              <option value="">Nessuna ubicazione selezionata</option>
              {locationsData?.locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {!selectedAssetId && !locationId && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <p className="text-amber-700 text-sm">Seleziona almeno un asset o una location per creare il ticket</p>
          </div>
        )}

        {createMutation.error && (
          <div className="p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-700 text-sm">{createMutation.error.message}</p>
          </div>
        )}

        <div className="flex gap-3 pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={() => navigate('/tickets')}
            className="btn btn-secondary flex-1"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending || !isFormValid}
            className="btn btn-primary flex-1"
          >
            {createMutation.isPending ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Creazione...
              </>
            ) : (
              'Crea Ticket'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
